import React from "react";
import {EdgeProps, getSmoothStepPath, Position} from "@xyflow/react";

function extendPoint(x1: number, y1: number, x2: number, y2: number, delta: number) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return [x1, y1];
    const ratio = delta / len;
    return [x1 - dx * ratio, y1 - dy * ratio];
}

const OVERSHOOT = 8;
const CURVE_RADIUS = 10;
const VERTICAL_DROP = 78;
const EPSILON = 1;

interface FamilyTreeEdgeData {
    anchor?: { x: number; y: number };
    partnerAnchor?: { source: { x: number; y: number }; target: { x: number; y: number } };
    verticalDrop?: number;
    [key: string]: any;
}

function isFinitePoint(point: { x: number; y: number } | undefined): boolean {
    return !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
}

function getPositionFromHandle(handleId?: string): Position | undefined {
    if (!handleId) return undefined;
    if (handleId === 'top') return Position.Top;
    if (handleId === 'bottom') return Position.Bottom;
    if (handleId === 'left') return Position.Left;
    if (handleId === 'right') return Position.Right;
    return undefined;
}

function getActualSourcePosition(
    sourceX: number,
    sourceY: number,
    data: any
): { x: number; y: number } {
    const edgeData = data as FamilyTreeEdgeData | undefined;
    const anchor = edgeData?.anchor;
    if (!anchor || !isFinitePoint(anchor)) {
        return { x: sourceX, y: sourceY };
    }
    return { x: anchor.x, y: anchor.y };
}

function getValidatedVerticalDrop(verticalDrop: number | undefined): number | undefined {
    if (verticalDrop === undefined || !Number.isFinite(verticalDrop)) {
        return undefined;
    }

    return Math.max(verticalDrop, CURVE_RADIUS);
}

function getPartnerAnchorPath(edgeData: FamilyTreeEdgeData | undefined): string | undefined {
    if (!edgeData?.partnerAnchor) {
        return undefined;
    }

    const {source, target} = edgeData.partnerAnchor;
    if (!isFinitePoint(source) || !isFinitePoint(target)) {
        return undefined;
    }

    return `M ${source.x},${source.y} L ${target.x},${target.y}`;
}

function getBottomToTopPath(
    actualSourceX: number,
    actualSourceY: number,
    targetX: number,
    targetY: number,
    edgeData: FamilyTreeEdgeData | undefined
): string {
    if (Math.abs(actualSourceX - targetX) < EPSILON) {
        return `M ${actualSourceX},${actualSourceY} L ${targetX},${targetY}`;
    }

    const customVerticalDrop = getValidatedVerticalDrop(edgeData?.verticalDrop);
    const verticalDrop = edgeData?.anchor
        ? VERTICAL_DROP
        : customVerticalDrop;
    const midY = verticalDrop !== undefined
        ? actualSourceY + verticalDrop
        : actualSourceY + (targetY - actualSourceY) * 0.5;
    const direction = targetX > actualSourceX ? 1 : -1;
    const vEndY = midY - CURVE_RADIUS;
    const c1x = actualSourceX;
    const c1y = midY;
    const c2x = actualSourceX + direction * CURVE_RADIUS;
    const c2y = midY;
    const hEndX = targetX - direction * CURVE_RADIUS;
    const c3x = targetX;
    const c3y = midY;
    const c4x = targetX;
    const c4y = midY + CURVE_RADIUS;

    return `M ${actualSourceX},${actualSourceY} L ${actualSourceX},${vEndY} C ${c1x},${c1y} ${c2x},${c2y} ${actualSourceX + direction * CURVE_RADIUS},${midY} L ${hEndX},${midY} C ${c3x},${c3y} ${c4x},${c4y} ${targetX},${midY + CURVE_RADIUS} L ${targetX},${targetY}`;
}

function getSmoothStepEdgePath(
    actualSourceX: number,
    actualSourceY: number,
    targetX: number,
    targetY: number,
    resolvedSourcePosition: Position,
    resolvedTargetPosition: Position
): string {
    const [exSourceX, exSourceY] = extendPoint(actualSourceX, actualSourceY, targetX, targetY, OVERSHOOT);
    const [exTargetX, exTargetY] = extendPoint(targetX, targetY, actualSourceX, actualSourceY, OVERSHOOT);

    const [edgePath] = getSmoothStepPath({
        sourceX: exSourceX,
        sourceY: exSourceY,
        sourcePosition: resolvedSourcePosition,
        targetX: exTargetX,
        targetY: exTargetY,
        targetPosition: resolvedTargetPosition,
        borderRadius: CURVE_RADIUS,
    });

    return edgePath;
}

function getEdgePath(
    actualSourceX: number,
    actualSourceY: number,
    targetX: number,
    targetY: number,
    resolvedSourcePosition: Position,
    resolvedTargetPosition: Position,
    edgeData: FamilyTreeEdgeData | undefined
): string {
    const partnerAnchorPath = getPartnerAnchorPath(edgeData);
    if (partnerAnchorPath) {
        return partnerAnchorPath;
    }

    if (resolvedSourcePosition === Position.Bottom && resolvedTargetPosition === Position.Top) {
        return getBottomToTopPath(actualSourceX, actualSourceY, targetX, targetY, edgeData);
    }

    return getSmoothStepEdgePath(
        actualSourceX,
        actualSourceY,
        targetX,
        targetY,
        resolvedSourcePosition,
        resolvedTargetPosition
    );
}

const FamilyTreeEdge: React.FC<EdgeProps> = (props) => {
    const {
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        style = {},
        markerEnd,
        markerStart,
        data
    } = props;

    const sourceHandle = (props as { sourceHandle?: string }).sourceHandle;
    const targetHandle = (props as { targetHandle?: string }).targetHandle;

    const resolvedSourcePosition = sourcePosition ?? getPositionFromHandle(sourceHandle) ?? Position.Bottom;
    const resolvedTargetPosition = targetPosition ?? getPositionFromHandle(targetHandle) ?? Position.Top;

    const { x: actualSourceX, y: actualSourceY } = getActualSourcePosition(sourceX, sourceY, data);
    const edgeData = data as FamilyTreeEdgeData | undefined;
    const edgePath = getEdgePath(
        actualSourceX,
        actualSourceY,
        targetX,
        targetY,
        resolvedSourcePosition,
        resolvedTargetPosition,
        edgeData
    );

    return (
        <g>
            <path
                d={edgePath}
                style={{...style, pointerEvents: 'stroke'}}
                className="family-tree-edge-path"
                markerEnd={markerEnd}
                markerStart={markerStart}
                data-testid="family-tree-edge-path"
            />
        </g>
    );
};

const areFamilyTreeEdgePropsEqual = (prev: EdgeProps, next: EdgeProps) => {
    return prev.sourceX === next.sourceX
        && prev.sourceY === next.sourceY
        && prev.targetX === next.targetX
        && prev.targetY === next.targetY
        && prev.sourcePosition === next.sourcePosition
        && prev.targetPosition === next.targetPosition
        && prev.markerEnd === next.markerEnd
        && prev.markerStart === next.markerStart
        && prev.data === next.data
        && prev.style === next.style;
};

export default React.memo(FamilyTreeEdge, areFamilyTreeEdgePropsEqual);